import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "react-oidc-context";

import { getUserRoles, getUsername, hasRealmRole } from "@/auth/roles";

interface Props {
  role: string;
  /** Куда отправлять без роли — для legacy compat (сейчас не используется). */
  fallback: string;
  children: ReactNode;
}

/** Читает роли из access_token. Поддерживаются оба провайдера:
 *  Keycloak (realm_access.roles + resource_access[*].roles) и Authentik
 *  (groups claim). id_token обычно не содержит ролей по умолчанию —
 *  поэтому декодируем именно access_token (см. auth/roles.ts). */
export default function RequireRole({ role, fallback, children }: Props) {
  const auth = useAuth();
  if (hasRealmRole(auth.user, role)) {
    return <>{children}</>;
  }
  const roles = getUserRoles(auth.user);
  const username = getUsername(auth.user);
  return (
    <div
      className="mt-empty"
      style={{
        margin: "var(--sp-4)",
        padding: "var(--sp-4)",
        textAlign: "left",
        maxWidth: 720,
      }}
    >
      <h2 style={{ margin: "0 0 var(--sp-2)" }}>Нет доступа</h2>
      <p style={{ margin: "0 0 var(--sp-2)" }}>
        Для просмотра этой страницы нужна роль{" "}
        <code className="mt-mono">{role}</code>.
      </p>
      <p
        className="mt-text-tight mt-muted"
        style={{ margin: "0 0 var(--sp-2)" }}
      >
        Залогинен как <strong>{username}</strong>. Текущие роли:
      </p>
      <pre
        className="mt-mono mt-text-tight"
        style={{
          background: "var(--panel-soft)",
          padding: "8px 12px",
          borderRadius: "var(--r-sm)",
          fontSize: "var(--fs-xs)",
          margin: "0 0 var(--sp-3)",
          maxHeight: 240,
          overflow: "auto",
        }}
      >
        {roles.length > 0 ? roles.join("\n") : "(пусто)"}
      </pre>
      <p
        className="mt-text-tight mt-muted"
        style={{ margin: "0 0 var(--sp-3)" }}
      >
        Чтобы добавить: в Authentik — Directory → Groups → создать/найти
        группу <code className="mt-mono">{role}</code>, добавить пользователя.
        В Keycloak — Realm roles → Create{" "}
        <code className="mt-mono">{role}</code>, затем User → Role mappings → assign.
      </p>
      <div style={{ display: "flex", gap: "var(--sp-2)" }}>
        <Link to={fallback} className="mt-btn mt-btn--secondary">
          К переводу
        </Link>
        <button
          className="mt-btn mt-btn--ghost"
          type="button"
          onClick={() => void auth.signoutRedirect()}
        >
          Выйти
        </button>
      </div>
    </div>
  );
}
